<template>
  <div class="uiv">
    <div class="otc-section">
      <div class="form-flex otc-actions">
        <div class="form-group form-group-lg">
          <button
            type="button"
            class="btn btn-block btn-default btn-lg"
            :disabled="disabled || otcRequestSent || resendSecondsRemaining > 0"
            @click="confirmAndSendOneTimeCode"
          >
            <span class="material-icons-outlined otc-btn-icon" aria-hidden="true">mail_outline</span>
            {{ resendSecondsRemaining > 0 ? `Resend in ${resendSecondsRemaining}s` : (otcCodeRequested ? 'Resend Code' : 'Send One-Time Code') }}
          </button>
        </div>
      </div>
      <div v-if="otcCodeRequested" class="form-flex otc-actions">
        <div class="form-group form-group-lg">
          <input
            type="text"
            name="one-time-code"
            v-model="otcCode"
            class="form-control fs-block otc-input"
            maxlength="10"
            placeholder="Enter code"
            aria-label="One-time code"
          >
        </div>
        <div class="form-group form-group-lg">
          <button
            type="button"
            class="btn btn-block btn-success btn-lg"
            :disabled="otcVerifySent || !otcCode"
            @click="verifyOneTimeCode"
          >
            Verify Code
          </button>
        </div>
      </div>

      <alert
        v-if="otcNotice"
        type="success"
        @dismissed="otcNotice = ''"
      >
        {{ otcNotice }}
      </alert>
      <alert
        v-if="otcError"
        type="danger"
        @dismissed="otcError = ''"
      >
        {{ otcError }}
      </alert>
    </div>

    <modal
      v-model="showOtcConfirm"
      :append-to-body="true"
      :backdrop="true"
      :footer="false"
      size="sm">
      <div slot="title">Send One-Time Code</div>
      <template slot="default">
        <p>Send a one-time code to {{ email }}?</p>
        <div class="form-flex">
          <button
            type="button"
            class="btn btn-default"
            @click="showOtcConfirm = false">Cancel</button>
          <button
            type="button"
            class="btn btn-success"
            @click="acceptOtcConfirm">Send Code</button>
        </div>
      </template>
    </modal>
  </div>
</template>

<script>
import { postOneTimeCodeRequest, postOneTimeCodeVerify } from '@/api/accounts'

const resendCountdownSeconds = 55

export default {
  name: 'OneTimeCode',
  props: {
    email: {
      type: String,
      required: true,
    },
    gblcs: {
      type: String,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      showOtcConfirm: false,
      otcCode: '',
      otcNotice: '',
      otcError: '',
      otcCodeRequested: false,
      otcRequestSent: false,
      otcVerifySent: false,
      resendSecondsRemaining: 0,
      resendIntervalId: null,
    }
  },
  methods: {
    confirmAndSendOneTimeCode() {
      if (this.resendSecondsRemaining > 0) return
      this.showOtcConfirm = true
    },
    acceptOtcConfirm() {
      this.showOtcConfirm = false
      this.sendOneTimeCode()
    },
    async sendOneTimeCode() {
      this.otcRequestSent = true
      this.otcCode = ''
      this.otcNotice = ''
      this.otcError = ''
      try {
        await postOneTimeCodeRequest(this.email, this.gblcs)
        this.otcCodeRequested = true
        this.$emit('otc-requested', true)
        this.otcNotice = `A sign-in code has been sent to ${this.email} if we found an account with that email.`
        this.startResendCountdown()
      } catch (err) {
        this.otcNotice = ''
        this.otcError = this.parseError(err, 'We could not send a one-time code right now.')
      } finally {
        this.otcRequestSent = false
      }
    },
    async verifyOneTimeCode() {
      const code = (this.otcCode || '').toString().trim().toUpperCase()
      this.otcCode = code
      this.otcError = ''

      if (!/^([A-Z0-9]{10})$/.test(code)) {
        this.otcError = 'Enter a valid 10-character code.'
        return
      }

      this.otcVerifySent = true
      try {
        await postOneTimeCodeVerify(this.email, code, this.gblcs)
        this.$sendEvent('Sign In Successful', { product: 'toolkit', ref: 'one_time_code' })
        this.$emit('login-success')
      } catch (err) {
        this.otcError = this.parseError(err, 'We could not verify your code. Please try again.')
      } finally {
        this.otcVerifySent = false
      }
    },
    parseError(err, fallback) {
      if (!err || !err.responseText) return fallback
      try {
        const parsed = JSON.parse(err.responseText)
        return parsed.message || err.responseText || fallback
      } catch {
        return err.responseText || fallback
      }
    },
    startResendCountdown() {
      this.clearResendCountdown()
      this.resendSecondsRemaining = resendCountdownSeconds
      this.resendIntervalId = setInterval(() => {
        this.resendSecondsRemaining -= 1
        if (this.resendSecondsRemaining <= 0) {
          this.clearResendCountdown()
        }
      }, 1000)
    },
    clearResendCountdown() {
      if (this.resendIntervalId) {
        clearInterval(this.resendIntervalId)
      }
      this.resendIntervalId = null
      if (this.resendSecondsRemaining < 0) {
        this.resendSecondsRemaining = 0
      }
    },
    reset() {
      this.clearResendCountdown()
      this.resendSecondsRemaining = 0
      this.otcCodeRequested = false
      this.$emit('otc-requested', false)
      this.otcCode = ''
      this.otcNotice = ''
      this.otcError = ''
      this.showOtcConfirm = false
    },
  },
  beforeDestroy() {
    this.clearResendCountdown()
  },
}
</script>

<style scoped lang="scss">
.form-flex {
  display: flex;
  justify-content: space-between;
  align-items: center;
  div {
    flex-basis: 40%;
  }
}

.otc-section {
  margin-top: 10px;
}

.otc-actions {
  justify-self: center;
  gap: 4px;
  .form-group-lg:first-of-type{
    flex-grow: 1;
  }
}

.otc-input {
  text-transform: uppercase;
  min-width: 10em;
  letter-spacing: 0.15em;
}

.otc-btn-icon {
  vertical-align: text-bottom;
  margin-right: 4px;
  font-size: 20px;
}
</style>
